import type { RequestOptions } from '@/lib/http'
import {
  buildFacetFilters,
  isReservedParam,
  HITS_PER_PAGE,
  resolveSort,
  searchFiltersFromParams,
  searchTutorials,
  transformFacets,
  type RouteFilter,
  type SearchFilters,
  type SortKey,
} from './search'
import type { Facet, TutorialHit } from './types'

/*
 * Data for the search listings (/tutorials, /:filter/:value and collection pages). The URL query string is
 * the single source of truth for the search state: route loaders read it on the server (and again on every
 * client-side change), the page component reads the same URL for its controls.
 */

export interface SearchState {
  filters: SearchFilters
  query: string
  page: number
  sort: SortKey
}

export interface SearchResults {
  hits: TutorialHit[]
  facets: Facet[]
  currentPage: number
  totalPages: number
  totalResults: number
}

export function searchStateFromParams(params: URLSearchParams): SearchState {
  const parsed = searchFiltersFromParams(params)
  const query = typeof parsed.q === 'string' ? parsed.q : ''
  const sort = resolveSort(parsed.sort, query)
  const page = Math.max(1, parseInt(String(parsed.page ?? '1'), 10) || 1)
  return { filters: { ...parsed, sort }, query, page, sort }
}

/**
 * True when the URL narrows or reorders the listing (text query, facet filters, non-default sort): such
 * variations are crawlable but not indexed. Plain pagination is not a variation.
 */
export function isSearchVariation(params: URLSearchParams): boolean {
  for (const key of new Set(params.keys())) {
    if (key === 'page') continue
    if (key === 'sort') {
      const query = params.get('q') ?? ''
      if (resolveSort(params.get('sort'), query) === resolveSort(undefined, query)) continue
    }
    return true
  }
  return false
}

/**
 * A listing's canonical path. A variation is noindex and its own canonical, since noindex with a canonical to another
 * URL is a mixed signal for Google. Otherwise the plain path, with the page number after page 1.
 */
export function listingCanonicalPath(pathname: string, params: URLSearchParams): string {
  if (isSearchVariation(params)) return `${pathname}?${params}`
  const { page } = searchStateFromParams(params)
  return page > 1 ? `${pathname}?page=${page}` : pathname
}

/**
 * The URL's search params without the keys the search doesn't know: tracking params (?_ga=, ?utm_source=), v1's
 * ?Type=, Shopify-style ?filter.skill_level=. The API ignores them, so they must not make a listing a variation or a
 * landing page. `facets` are the ones the API returned; without them every key is kept.
 */
export function knownSearchParams(params: URLSearchParams, facets: readonly Facet[]): URLSearchParams {
  if (facets.length === 0) return params
  const names = new Set(facets.map((f) => f.field))
  return new URLSearchParams([...params].filter(([key]) => isReservedParam(key) || names.has(key)))
}

/**
 * The search params an old storefront filter link (?filter.skill_level=Beginner) stands for: each filter under
 * its facet's name, plus the text query. Null when there are no such filters, or one names a facet or value the
 * search doesn't have, so the link is left as an ignored param.
 */
export function legacyFilterParams(params: URLSearchParams, facets: readonly Facet[]): URLSearchParams | null {
  const legacy = [...params].filter(([key]) => key.startsWith('filter.'))
  if (legacy.length === 0) return null
  const next = new URLSearchParams()
  for (const [key, value] of legacy) {
    const name = key.slice('filter.'.length).replace(/_/g, ' ').toLowerCase()
    const facet = facets.find((f) => f.field.toLowerCase() === name)
    if (!facet?.options.some((o) => o.value === value)) return null
    next.append(facet.field, value)
  }
  const query = params.get('q')
  if (query) next.set('q', query)
  return next
}

export async function loadSearchResults(
  params: URLSearchParams,
  route: RouteFilter,
  opts: RequestOptions = {},
): Promise<SearchResults> {
  const { filters, query, page, sort } = searchStateFromParams(params)
  const res = await searchTutorials(
    { query, filters: buildFacetFilters(filters, route), sort, page, perPage: HITS_PER_PAGE },
    opts,
  )
  return {
    hits: res.data ?? [],
    facets: transformFacets(res.facets),
    currentPage: res.meta?.page ?? page,
    totalPages: res.meta?.total_pages ?? 0,
    totalResults: res.meta?.total ?? 0,
  }
}

export interface FacetFilter {
  facet: string
  value: string
}

/**
 * The filter of a plain single-filter listing (/tutorials?Instructors=Jenny+Doan, optionally ?page=N), else null.
 * v1 linked these from its menus and Google ranks many of them, so they are landing pages, not variations.
 */
export function singleFacetFilter(params: URLSearchParams): FacetFilter | null {
  const keys = [...new Set(params.keys())].filter((k) => k !== 'page')
  if (keys.length !== 1 || isReservedParam(keys[0])) return null
  const values = params.getAll(keys[0]).filter(Boolean)
  return values.length === 1 ? { facet: keys[0], value: values[0] } : null
}
